import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { CONNECTOR_PLATFORM_LABELS } from '@/entities/media-connector';
import { countRows, flattenNodes, type JobPlatformGroup } from '@/entities/media-campaign';

interface PublishConfirmDialogProps {
  open: boolean;
  groups: JobPlatformGroup[];
  isPublishing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Confirms a publish before it writes to live ad accounts.
 *
 * Publishing is the one action here that leaves this system: it creates campaigns that can
 * start spending, and nothing in the app can take them back — the plan is editable up to
 * this point and the objects are not. That asymmetry is why the step exists.
 *
 * It names the accounts and the counts rather than asking "are you sure": the mistake worth
 * catching is not a stray click but a plan pointed at the wrong ad account, and the only
 * thing that catches that is seeing the account's name before it goes.
 */
export function PublishConfirmDialog({
  open,
  groups,
  isPublishing,
  onCancel,
  onConfirm,
}: PublishConfirmDialogProps) {
  const { t } = useTranslation('projects');

  // Objects, not rows: a plan of twelve ads creates sixteen once its campaign and ad sets
  // are counted, and more once each ad's creative is. The review folds a creative into its
  // ad's row, but the platform gets it as an object of its own, written before the ad.
  const objects = groups.reduce((total, group) => {
    const nodes = flattenNodes(group.nodes);
    return total + nodes.length + nodes.filter((node) => node.creativeNodeId).length;
  }, 0);
  const ads = groups.reduce((total, group) => total + countRows(group), 0);

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isPublishing) onCancel();
      }}
    >
      <AlertDialogContent data-testid="publish-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('deliveryJobReview.publishConfirmDialog.title', { count: ads })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('deliveryJobReview.publishConfirmDialog.detail', { count: objects })}
            {/* Inside the description so it is announced when the dialog opens; spans,
                because Radix renders the description as a <p>. One line per platform, so a
                mixed plan reads as one platform being different from the other. */}
            {groups
              .filter((group) => group.dryRunSupported === false)
              .map((group) => (
                <span
                  key={group.platform}
                  className="mt-1 block text-warning-foreground"
                  data-testid={`publish-confirm-not-checked-${group.platform}`}
                >
                  {t('deliveryJobReview.publishConfirmDialog.notChecked', {
                    platform: CONNECTOR_PLATFORM_LABELS[group.platform],
                  })}
                </span>
              ))}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Outside the description: a list inside it would be read as one run-on sentence,
            and the account name is the part worth reading slowly. */}
        <ul className="flex flex-col gap-1 text-sm">
          {groups.map((group) => (
            <li key={group.platform} className="flex justify-between gap-4">
              <span className="text-foreground">{CONNECTOR_PLATFORM_LABELS[group.platform]}</span>
              <span className="text-muted-foreground">
                {group.accountName ?? t('deliveryJobReview.publishConfirmDialog.noAccountNamed')}
              </span>
            </li>
          ))}
        </ul>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPublishing}>
            {t('deliveryJobReview.publishConfirmDialog.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction disabled={isPublishing} onClick={onConfirm}>
            {isPublishing
              ? t('deliveryJobReview.publishConfirmDialog.publishing')
              : t('deliveryJobReview.publishConfirmDialog.publish')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
